import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { cx } from '@emotion/css';

import { IEyeParamsOption } from '../../../graphql/lenses/models/get-lenses.models';
import { IEyeParamsProps } from './eye-params.models';
import { useStyles } from './eye-params.styles';
import { getParamLabelTranslationId, getUnits } from './utils';
import { useFormattedValue } from './hooks';

export const EyeParams = ({
  params, title, isOrder,
}: IEyeParamsProps): JSX.Element => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { getFormattedValue } = useFormattedValue();

  const capitalizeFirstLetter = (text: string): string => (
    text ? `${text.charAt(0).toUpperCase()}${text.slice(1)}` : text
  );

  const formattedParams = useMemo(
    () => params.map(({ value, property }: IEyeParamsOption) => ({
      property,
      value: getFormattedValue(value, property),
      label: capitalizeFirstLetter(formatMessage({ id: getParamLabelTranslationId(property) })),
      units: getUnits(property),
    })), [formatMessage, getFormattedValue, params],
  );

  return (
    <>
      {isOrder ? (
        <>
          <h4
            data-testid="order-params-title"
            className={cx(classes.titleOrder, classes.inline)}
          >
            {title}
          </h4>
          <p
            data-testid="order-params-wrapper"
            className={cx(classes.paramOrder, classes.inline)}
          >
            {formattedParams.map(({ value, property, label, units }) => (
              <span
                key={property}
                data-testid="eye-params-item"
                className={classes.paramItemOrder}
              >
                <span
                  data-testid="eye-property"
                  className={classes.paramPropertyOrder}
                >
                  {label}
                  {' '}
                </span>
                <span data-testid="eye-value">
                  {value}
                  {units}
                </span>
                <span data-testid="comma">{', '}</span>
              </span>
            ))}
          </p>
        </>
      ) : (
        <div>
          <h4 className={classes.title}>
            {title}
          </h4>
          {formattedParams.map(({ value, property, label, units }) => (
            <p
              className={classes.param}
              key={property}
              data-testid="eye-params-item"
            >
              {label}
              {': '}
              <span className={classes.paramValue}>
                {value}
                {units}
              </span>
            </p>
          ))}
        </div>
      )}
    </>
  );
};
